import { groupPracticesBySubject, type Subjected } from '../../lib/group-practices-by-subject.js'

/**
 * How the student's home arranges what it has to show: which practice comes
 * first inside a subject, which subject comes first, and which subjects open
 * on their own.
 *
 * Every decision is read from what the server already said about each row —
 * `canStart`, `availableUntil`, whether it has been attempted — and none of it
 * is a new status. "Can I start this now" is the server's answer; this only
 * decides where that answer is put so the student sees it without hunting.
 *
 * There is deliberately no "ending soon" threshold. Athena has no rule for
 * how soon is soon, and inventing one here would be a business rule living in
 * a layout helper. The date a practice closes is what is used instead: a
 * practice with a closing date sorts ahead of one without, the earliest first,
 * so whatever closes next is at the top of its subject and its subject is
 * near the top of the page.
 */
export interface ShelfRow extends Subjected {
  practiceId: string
  title: string
  canStart: boolean
  availableUntil: string | null
  /** True once the student has an attempt at it, so the action is another go rather than a first. */
  attempted: boolean
}

/**
 * At most this many subjects open on their own. Beyond it, the rest wait
 * folded — each still saying in its header how many practices in it can be
 * started — so a student with work in eight subjects is not handed a page of
 * eight open grids. Three is a screenful on a phone.
 */
export const MAX_OPEN_SUBJECTS = 3

/** A closing date as a sortable number; no closing date sorts after every date. */
function closes(row: ShelfRow): number {
  return row.availableUntil === null ? Number.POSITIVE_INFINITY : Date.parse(row.availableUntil)
}

/**
 * The order inside one subject: what can be started before what cannot; among
 * those, whatever closes first; then a first go before another go; then by
 * title, so two practices that tie always come out in the same order.
 */
export function orderForStudent<T extends ShelfRow>(rows: readonly T[], language: string): T[] {
  return [...rows].sort(
    (a, b) =>
      Number(b.canStart) - Number(a.canStart) ||
      (a.canStart ? closes(a) - closes(b) : 0) ||
      (a.canStart ? Number(a.attempted) - Number(b.attempted) : 0) ||
      a.title.localeCompare(b.title, language) ||
      a.practiceId.localeCompare(b.practiceId),
  )
}

export interface ShelfGroup<T extends ShelfRow> {
  subject: T['subject']
  practices: readonly T[]
  /** How many of them the student can start, or have another go at, right now. */
  available: number
  /** Whether the subject starts open. Decided once, when the home first draws it. */
  open: boolean
}

/**
 * The subjects in the order a student needs them, each with its practices in
 * order and whether it starts open.
 *
 * Subjects with something to start come first, the one whose next closing
 * date is soonest leading, then the rest in the catalog's own order — so a
 * subject keeps its place from one visit to the next unless what can be done
 * in it changes. Only subjects with something to start open, and only the
 * first `MAX_OPEN_SUBJECTS` of those.
 */
export function shelve<T extends ShelfRow>(rows: readonly T[], language: string): ShelfGroup<T>[] {
  const groups = groupPracticesBySubject(rows).map((group) => {
    const practices = orderForStudent(group.practices, language)
    const available = practices.filter((row) => row.canStart).length
    // The first row is the soonest-closing startable one, when there is one.
    const next = available > 0 && practices[0] ? closes(practices[0]) : Number.POSITIVE_INFINITY
    return { subject: group.subject, practices, available, next }
  })

  const ordered = [
    ...groups.filter((group) => group.available > 0).sort((a, b) => a.next - b.next),
    ...groups.filter((group) => group.available === 0),
  ]

  // `sort` is stable, so subjects tied on their closing date keep the
  // catalog order `groupPracticesBySubject` gave them.
  return ordered.map(({ next: _next, ...group }, index) => ({
    ...group,
    open: group.available > 0 && index < MAX_OPEN_SUBJECTS,
  }))
}
